import { clstx } from '@payaca/utilities/cvaMerge';
import React, { FC } from 'react';
import ReactMarkdown, { Options } from 'react-markdown';
import rehypeExternalLinks from 'rehype-external-links';
import remarkGfm from 'remark-gfm';

type TMarkdownCoreProps = {
  markdown: string;
} & Options;

/*
  Apply the design-system type scale to rendered Markdown so it matches authored
  JSX. We use raw tags with the shared `ambrosia-*` classes rather than the
  <Heading>/<Text> components: those components intentionally expose no
  `className`, but the Markdown pipeline must forward classes injected by remark
  plugins onto the element (e.g. `whitespace-pre-wrap` from
  remarkPreserveLegacyNewlines). The classes are the same ones the components
  use. Consumer-supplied `components` override these defaults.
*/
const typographyComponents: Options['components'] = {
  h1: ({ node, className, ...props }) => (
    <h1 className={clstx('ambrosia-h1', className)} {...props} />
  ),
  h2: ({ node, className, ...props }) => (
    <h2 className={clstx('ambrosia-h2', className)} {...props} />
  ),
  h3: ({ node, className, ...props }) => (
    <h3 className={clstx('ambrosia-h3', className)} {...props} />
  ),
  h4: ({ node, className, ...props }) => (
    <h4 className={clstx('ambrosia-h4', className)} {...props} />
  ),
  h5: ({ node, className, ...props }) => (
    <h5 className={clstx('ambrosia-h4', className)} {...props} />
  ),
  h6: ({ node, className, ...props }) => (
    <h6 className={clstx('ambrosia-h4', className)} {...props} />
  ),
  p: ({ node, className, ...props }) => (
    <p className={clstx('ambrosia-body', className)} {...props} />
  ),
};

const MarkdownCore: FC<TMarkdownCoreProps> = (props) => {
  const { markdown, remarkPlugins, rehypePlugins, components, ...rest } = props;

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm, ...(remarkPlugins ?? [])]}
      rehypePlugins={[
        [
          rehypeExternalLinks,
          {
            target: '_blank',
            rel: ['noopener', 'noreferrer', 'nofollow'],
          },
        ],
        ...(rehypePlugins ?? []),
      ]}
      components={{ ...typographyComponents, ...components }}
      {...rest}
    >
      {markdown}
    </ReactMarkdown>
  );
};

export default MarkdownCore;
